import { useState, useEffect } from "react";
import { Button, Input, Label, Text, AlertBanner } from "snap-design-system";
import { Check, Eye, EyeOff, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCredentialsStoreActions } from "~/store/credentials";
import { useEncryption } from "~/hooks/useEncryption";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { useUserData } from "~/store/userStore";
import { useDialogActions } from "~/store/dialogStore";
import { startNewAuthorizedSession } from "~/store/auth/events";
import { arrayBufferToBase64, convertDurationToDate } from "~/helpers";
import { verifierPhrase } from "~/helpers/constants";
import { encryptionWorker } from "~/services/workers/encryptionWorker.service";
import { useUserRewrapWs } from "~/hooks/useUserRewrapWs";
import { validatePasswordNIST } from "~/components/PasswordStrengthMeter";
import PasswordStrengthMeter from "~/components/PasswordStrengthMeter";
import { StorageDurationSelector } from "./SecretKeyDialog";
import { ReencryptionProgress } from "~/containers/account/configuration/security/ReencryptionProgress";

/**
 * Shared ref type used to pass form values from Content → Footer
 * without causing re-render issues when the dialog JSX is captured as a snapshot.
 */
export interface MandatoryChangeFormRef {
    newPassword: string;
    confirmPassword: string;
    duration: string;
}

interface MandatoryChangeSecretKeyContentProps {
    formRef: React.MutableRefObject<MandatoryChangeFormRef>;
}

export function MandatoryChangeSecretKeyContent({ formRef }: MandatoryChangeSecretKeyContentProps) {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [duration, setDuration] = useState("15m");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleNewPassword = (v: string) => {
        setNewPassword(v);
        formRef.current.newPassword = v;
    };

    const handleConfirmPassword = (v: string) => {
        setConfirmPassword(v);
        formRef.current.confirmPassword = v;
    };

    const handleDuration = (v: string) => {
        setDuration(v);
        formRef.current.duration = v;
    };

    return (
        <div className="space-y-4">
            <AlertBanner
                variant="information"
                icon={<Info className="size-5" />}
                title="Alteração de senha obrigatória"
                message="Sua senha de criptografia pessoal foi redefinida por um administrador. Crie uma nova senha para continuar."
            />

            <div className="space-y-3">
                <div>
                    <Label>Nova senha:</Label>
                    <div className="relative">
                        <Input
                            type={showNew ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => handleNewPassword(e.target.value)}
                            placeholder="Digite sua nova senha"
                            fullWidth
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => setShowNew(!showNew)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100 cursor-pointer"
                        >
                            {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <PasswordStrengthMeter password={newPassword} />
                </div>

                <div>
                    <Label>Confirmar nova senha:</Label>
                    <div className="relative">
                        <Input
                            type={showConfirm ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => handleConfirmPassword(e.target.value)}
                            placeholder="Confirme sua nova senha"
                            fullWidth
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-80 hover:opacity-100 cursor-pointer"
                        >
                            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {confirmPassword && newPassword !== confirmPassword && (
                        <Text variant="body-sm" className="text-red-500 mt-1">
                            As senhas não coincidem.
                        </Text>
                    )}
                </div>
            </div>

            <StorageDurationSelector value={duration} onChange={handleDuration} />
        </div>
    );
}

interface MandatoryChangeSecretKeyFooterProps {
    currentTempPassword: string;
    formRef: React.MutableRefObject<MandatoryChangeFormRef>;
    onSuccess?: () => void;
}

export function MandatoryChangeSecretKeyFooter({
    currentTempPassword,
    formRef,
    onSuccess,
}: MandatoryChangeSecretKeyFooterProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [toastId, setToastId] = useState<string | number | null>(null);
    const { persistPassword, updatePassword } = useCredentialsStoreActions();
    const { deriveKey } = useEncryption();
    const userData = useUserData();
    const { closeDialog } = useDialogActions();
    const { clearMustChangeSecretKeyMutation } = useUserCRUD();
    const { start: startRewrap, progress: rewrapProgress, isComplete: rewrapComplete } = useUserRewrapWs();

    // Keep toast in sync with progress — only update, never create a new one here
    useEffect(() => {
        if (!isProcessing || !toastId) return;

        const { total, processed, failed } = rewrapProgress;
        if (total === 0) return; // Still waiting for WS — initial toast already shown

        toast.loading("Re-criptografando chaves...", {
            id: toastId,
            description: `${processed}/${total} concluídos${failed > 0 ? ` (${failed} falha(s))` : ""}`,
            duration: Infinity,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rewrapProgress, isProcessing]);

    // When WS re-wrap completes, finalize the password change
    useEffect(() => {
        if (!rewrapComplete || !isProcessing) return;

        (async () => {
            try {
                const { newPassword, duration } = formRef.current;
                const newRawKey = await deriveKey(newPassword);
                const newKeyBytes = newRawKey.buffer.slice(
                    newRawKey.byteOffset, newRawKey.byteOffset + newRawKey.byteLength,
                ) as ArrayBuffer;

                const newVerifier = await encryptionWorker.encrypt(verifierPhrase, newKeyBytes);
                await clearMustChangeSecretKeyMutation.mutateAsync(newVerifier);

                const storedKey = arrayBufferToBase64(newKeyBytes);
                updatePassword(storedKey);
                const expirationDate = convertDurationToDate(duration || "15m");
                if (expirationDate) persistPassword(expirationDate);

                if (toastId) {
                    toast.success("Senha alterada com sucesso!", {
                        id: toastId,
                        description: `${rewrapProgress.processed} chave(s) re-criptografada(s)`,
                        duration: 5000,
                    });
                } else {
                    toast.success("Senha alterada com sucesso!");
                }

                startNewAuthorizedSession(userData?.roles || []);
                closeDialog();
                onSuccess?.();
            } catch (error) {
                console.error("[MandatoryChange] Finalize error:", error);
                if (toastId) {
                    toast.error("Erro ao finalizar alteração de senha.", { id: toastId, duration: 8000 });
                } else {
                    toast.error("Erro ao finalizar alteração de senha.");
                }
            } finally {
                setIsProcessing(false);
                setToastId(null);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rewrapComplete]);

    const handleChangePassword = async () => {
        const { newPassword, confirmPassword } = formRef.current;

        if (!newPassword.trim()) { toast.error("A nova senha não pode estar vazia."); return; }
        if (newPassword !== confirmPassword) { toast.error("As senhas não coincidem."); return; }
        const validation = validatePasswordNIST(newPassword);
        if (!validation.isValid) { toast.error("Senha insegura", { description: validation.errorMessage }); return; }

        setIsProcessing(true);
        try {
            const oldRawKey = await deriveKey(currentTempPassword);
            const oldKeyBytes = oldRawKey.buffer.slice(oldRawKey.byteOffset, oldRawKey.byteOffset + oldRawKey.byteLength) as ArrayBuffer;

            const newRawKey = await deriveKey(newPassword);
            const newKeyBytes = newRawKey.buffer.slice(newRawKey.byteOffset, newRawKey.byteOffset + newRawKey.byteLength) as ArrayBuffer;

            const userId = userData?.user_id;
            if (!userId) throw new Error("User ID not found");

            // Show initial toast immediately — before WS connects
            const id = toast.loading("Preparando re-criptografia...", { duration: Infinity });
            setToastId(id);

            startRewrap(userId, oldKeyBytes, newKeyBytes);
        } catch (error) {
            console.error("[MandatoryChange] Error:", error);
            toast.error("Erro ao alterar senha.", {
                description: error instanceof Error ? error.message : "Tente novamente.",
            });
            setIsProcessing(false);
        }
    };

    const showProgress = isProcessing && rewrapProgress.total > 0;

    return (
        <div className="flex flex-col gap-3 w-full">
            {showProgress && (
                <ReencryptionProgress progress={{
                    total: rewrapProgress.total,
                    completed: rewrapProgress.processed,
                    failed: rewrapProgress.failed,
                    isRunning: rewrapProgress.isRunning,
                }} />
            )}
            {isProcessing && rewrapProgress.total === 0 && (
                <div className="flex items-center gap-2 text-sm text-subtle">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Preparando re-criptografia...</span>
                </div>
            )}
            <div className="flex justify-end w-full">
                <Button
                    onClick={handleChangePassword}
                    disabled={isProcessing}
                    icon={isProcessing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    iconPosition="right"
                >
                    {isProcessing
                        ? rewrapProgress.total > 0
                            ? `Re-criptografando (${rewrapProgress.processed}/${rewrapProgress.total})...`
                            : "Preparando..."
                        : "Confirmar nova senha"}
                </Button>
            </div>
        </div>
    );
}

export const MandatoryChangeSecretKeyDialog = {
    Content: MandatoryChangeSecretKeyContent,
    Footer: MandatoryChangeSecretKeyFooter,
};
